import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { getBaseUrl, resolveOgImageUrl } from "@/lib/seo";

const THEME_COLOR = import.meta.env.VITE_THEME_COLOR?.trim() || "#6366f1";
const GOOGLE_SITE_VERIFICATION = import.meta.env.VITE_GOOGLE_SITE_VERIFICATION?.trim();
const FACEBOOK_DOMAIN_VERIFICATION = import.meta.env.VITE_FACEBOOK_DOMAIN_VERIFICATION?.trim();
const MS_VALIDATE = import.meta.env.VITE_MS_VALIDATE?.trim();

export interface SeoHeadProps {
  /** Título da página (document title e og:title). */
  title: string;
  /** Meta description (e og:description, twitter:description). */
  description?: string;
  /** Caminho canônico (ex.: "/" ou "/planos"). Monta canonical e og:url com getBaseUrl(). */
  canonicalPath?: string;
  /** Se true, adiciona meta robots noindex,nofollow. */
  noindex?: boolean;
  /** URL absoluta ou path (ex.: /og-fabchat.png). Omitir = imagem padrão do SaaS no domínio atual. */
  image?: string;
  /** Tipo og (padrão: "website"). */
  ogType?: "website" | "article";
}

/**
 * Head reutilizável por página: title, description, canonical, og:* e twitter:*.
 * Usa VITE_APP_URL para URLs absolutas. Em rotas noindex preenche og:url/twitter:url com a URL atual.
 */
export function SeoHead({
  title,
  description,
  canonicalPath,
  noindex = false,
  image,
  ogType = "website",
}: SeoHeadProps) {
  const baseUrl = getBaseUrl();
  const ogImageUrl = resolveOgImageUrl(baseUrl, image);
  const { pathname } = useLocation();

  const canonicalUrl =
    canonicalPath != null && baseUrl
      ? `${baseUrl}${canonicalPath.startsWith("/") ? canonicalPath : `/${canonicalPath}`}`
      : undefined;

  /** Para rotas noindex sem canonicalPath: og:url e twitter:url com a URL absoluta da página atual. */
  const socialUrl =
    baseUrl && (canonicalUrl || (noindex && pathname != null))
      ? canonicalUrl ?? `${baseUrl}${pathname.startsWith("/") ? pathname : `/${pathname}`}`
      : undefined;

  const siteName = "FabChat";

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="theme-color" content={THEME_COLOR} />
      <meta name="msapplication-TileColor" content={THEME_COLOR} />
      <meta
        name="msapplication-TileImage"
        content={`${baseUrl.replace(/\/+$/, "")}/icons/app-icon-512.png`}
      />
      {GOOGLE_SITE_VERIFICATION ? (
        <meta name="google-site-verification" content={GOOGLE_SITE_VERIFICATION} />
      ) : null}
      {FACEBOOK_DOMAIN_VERIFICATION ? (
        <meta name="facebook-domain-verification" content={FACEBOOK_DOMAIN_VERIFICATION} />
      ) : null}
      {MS_VALIDATE ? <meta name="msvalidate.01" content={MS_VALIDATE} /> : null}
      {description != null && description !== "" && (
        <meta name="description" content={description} />
      )}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:site_name" content={siteName} />
      {description != null && description !== "" && (
        <meta property="og:description" content={description} />
      )}
      {socialUrl && <meta property="og:url" content={socialUrl} />}
      <meta property="og:image" content={ogImageUrl} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@fabia" />
      <meta name="twitter:title" content={title} />
      {description != null && description !== "" && (
        <meta name="twitter:description" content={description} />
      )}
      {socialUrl && <meta name="twitter:url" content={socialUrl} />}
      <meta name="twitter:image" content={ogImageUrl} />
    </Helmet>
  );
}
